<template>
    <vx-box color="info" class="flex flex-col">
        <div
            class="bg-slate-100 border-b border-solid border-t-0 border-x-0 border-slate-200 flex flex-row py-4 px-6 items-center gap-2 justify-between md:rounded-t-2xl"
        >
            <span v-if="loading && !activeMessage" class="text-xl font-extrabold leading-tight">Loading...</span>
            <div v-else class="flex flex-col">
                <div class="mb-2 md:hidden">
                    <router-link :to="{ name: 'message-hub.scheduled-messages' }" class="text-base">
                        <font-awesome-icon widthAuto :icon="faArrowLeft"></font-awesome-icon>
                        Scheduled Messages
                    </router-link>
                </div>
                <span class="font-extrabold text-xl text-slate-700">{{ contactTitle }}</span>
                <span v-if="contactPhone" class="text-sm text-slate-500">{{ contactPhone }}</span>
            </div>

            <div v-if="activeMessage">
                <vx-button @click="removeScheduledMessage()" size="md" type="button" color="muted">
                    Delete
                    <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                </vx-button>
            </div>
        </div>

        <div v-if="loading && !activeMessage" class="flex items-center justify-center h-full">
            <vx-icon-loading spin></vx-icon-loading>
        </div>
        <scheduled-message-content
            v-else-if="activeMessage"
            :key="activeMessage.id"
            ref="contentRef"
            v-model="activeMessage"
            v-model:error="sendingError"
            @selected:snippet="selectedSnippetBody = $event.body"
            :blockImages="blockImages"
            :blockFiles="blockFiles"
            class="p-8 overflow-auto scroll-thin"
        ></scheduled-message-content>

        <div class="rounded-b-2xl bg-white flex flex-row p-6 items-start gap-2 justify-between">
            <div class="flex flex-col-reverse gap-4 w-full lg:flex-row">
                <router-button
                    data-test="cancel-btn"
                    size="lg"
                    color="muted"
                    :to="{ name: 'message-hub.scheduled-messages' }"
                >
                    Cancel
                </router-button>
                <vx-button
                    @click="updateScheduledMessage"
                    :loading="sending"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                    data-test="update-btn"
                >
                    Update <font-awesome-icon widthAuto :icon="faSave"></font-awesome-icon>
                </vx-button>
            </div>
        </div>
    </vx-box>
</template>

<script setup>
import { faSave, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { VxBox, VxButton, VxIconLoading, VxSwal } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useScheduledMessages } from '~/components/message-hub/useScheduledMessages';
import { contactSubtitle, displayContactName } from '~/components/filters';
import ScheduledMessageContent from '~/components/scheduled-messages/ScheduledMessageContent.vue';
import useVuelidate from '@vuelidate/core';
import { computed, nextTick, ref, watch } from 'vue';
import { actionsClient } from '~/services';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import RouterButton from '~/components/general/RouterButton.vue';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { throwSwalRequestError } from '~/utils/request';
import { validateRules } from '~/utils/validation';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useMessageHubRules } from '~/composables/useMessageHubRules';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const router = useRouter();
const route = useRoute();

const teamId = Spark.state.currentTeam.id;
const { getCurrentGroupId } = useCurrentGroup();

const {
    editScheduledMessage,
    deleteScheduledMessage,
    activeMessage,
    setActiveMessage,
    sending,
    sendingError,
    scheduledMessages,
    loading,
} = useScheduledMessages();

const { blockImages, blockFiles, getMessageHubRules } = useMessageHubRules();

watch(
    () => activeMessage.value?.group_id,
    (groupId) => {
        getMessageHubRules(groupId);
    },
    { immediate: true }
);

const selectedSnippetBody = ref('');
const contentRef = ref();

const validate = useVuelidate();

const contactTitle = computed(() =>
    activeMessage.value?.contact ? displayContactName(activeMessage.value.contact) : null
);
const contactPhone = computed(() =>
    activeMessage.value?.contact ? contactSubtitle(activeMessage.value.contact) : null
);

const updateScheduledMessage = async () => {
    if (sending.value) {
        return;
    }

    const valid = await validateRules(validate);

    if (!valid) {
        contentRef.value?.scrollToTop();
        return;
    }

    editScheduledMessage({ teamId, groupId: getCurrentGroupId() }, activeMessage.value).then((data) => {
        if (sendingError.value) {
            contentRef.value?.scrollToTop();
            return;
        }

        if (selectedSnippetBody.value && activeMessage.value.body.includes(selectedSnippetBody.value)) {
            actionsClient.send('snippet_used');
        }

        toasted.success('Scheduled Message updated');

        router.replace({
            name: route.name,
            params: { scheduledMessageId: data.id },
        });
    });
};

const removeScheduledMessage = async () => {
    VxSwal.confirmation('Delete Scheduled Message', 'Are you sure? There is no undo.', 'danger', () =>
        deleteScheduledMessage(
            { teamId, groupId: getCurrentGroupId() },
            activeMessage.value.id,
            activeMessage.value.contact.id
        )
            .then(() => {
                scheduledMessages.value = scheduledMessages.value.filter((m) => m.id !== activeMessage.value.id);
                router.replace({
                    name: 'message-hub.scheduled-messages',
                });
            })
            .catch(throwSwalRequestError)
    );
};

watch(loading, () => {
    if (loading.value) {
        return;
    }

    // set an active message right after the first page was loaded
    setActiveMessage();

    if (!activeMessage.value) {
        router.replace({
            name: 'message-hub.scheduled-messages',
        });
    }
});

watch(
    () => route.params.scheduledMessageId,
    () => {
        setActiveMessage();
    },
    { immediate: true }
);

watch(
    () => activeMessage.value,
    async (newMessage, oldMessage) => {
        if (newMessage?.id !== oldMessage?.id) {
            await nextTick();
            validate.value.$reset();
            sendingError.value = '';
        }
    },
    {
        immediate: true,
        deep: true,
    }
);
</script>
